<template>
  <el-dialog
    custom-class="article-dialog"
    :close-on-click-modal="true"
    :destroy-on-close="true"
    :show-close="false"
    :visible.sync="dialogVisible"
    :before-close="handleClose"
    @close="handleClose"
  >
    <div slot="title" class="article-dialog-header-room">
      <!-- 移动端：返回按钮放进标题栏参与 flex 布局，
           比原来的绝对定位更稳，也不会压住作者头像 -->
      <div class="dialog-back-btn" v-if="isMobile" @click="handleClose">
        <i class="el-icon-arrow-left"></i>
      </div>
      <div 
        class="author-info-wrapper" 
        :class="{ 'clickable': !isAnonymous }"
        @click="handleAuthorClick"
      >
        <smart-img
          v-if="dataLoaded && articleUserInfo.avatar"
          mode="avatar"
          :src="articleUserInfo.avatar"
          :alt="(articleUserInfo || {}).nickname"
          eager
          class="article-dialog-user-avatar"
        />
        <div v-else class="article-dialog-user-avatar avatar-placeholder"></div>
        <div class="article-dialog-user-info">
          <div class="article-dialog-name-text">
            <span class="name-text">
              {{ (articleUserInfo || {}).nickname }}
              <span class="nick-type" v-if="isAnonymous"
                ><svg-icon icon-class="nickred" size="18"
              /></span>
            </span>
          </div>
          <div class="article-dialog-user-tag">
            <span class="article-dialog-tag-text">{{
              formattedCreateTime
            }}</span>
            <span 
              v-if="articleUserInfo.school" 
              class="article-dialog-tag-text school-with-badge"
            >
              {{ articleUserInfo.school }}
              <!-- 匿名身份不展示学校认证角标 -->
              <verified-badge
                v-if="!isAnonymous"
                :verified="articleUserInfo.campusVerified"
                :size="13"
              />
            </span>
          </div>
        </div>
      </div>
      <div style="margin-left: auto">
        <!-- 三态：关注 / 已申请（对方是私密账号，等待同意）/ 已关注 -->
        <el-button 
          v-if="shouldShowFollowButton"
          :class="['follow-button', {
            'is-followed': followStatus === FOLLOW_STATUS.FOLLOWED,
            'is-pending': followStatus === FOLLOW_STATUS.PENDING
          }]"
          size="small"
          @click="handleFollowToggle"
        >
          <span v-if="followStatus === FOLLOW_STATUS.NONE" class="plus-icon">+</span>
          <span>{{ followLabel }}</span>
        </el-button>
      </div>
    </div>

    <div class="article-dialog-content" v-if="articleId">
      <ArticleInfo 
        ref="articleInfo"
        :articleId="articleId" 
        :commentId="commentId" 
        :mobileToolbar="isMobile"
        @article-loaded="handleArticleLoaded"
        @article-updated="handleArticleUpdated"
        @auth-failed="handleClose"
      />
    </div>

    <!-- 移动端底部操作条：评论入口常驻，点赞/评论/收藏不用滚回正文 -->
    <div class="article-mobile-bar" v-if="isMobile && articleId">
      <div class="article-mobile-bar__write" @click="handleFocusComment">
        <svg-icon icon-class="edit" size="14" />
        <span>说点什么...</span>
      </div>
      <div class="article-mobile-bar__acts">
        <span
          class="bar-act"
          :class="{ 'is-on': barArticle.isLike }"
          @click="handleBarLike"
        >
          <svg-icon :icon-class="barArticle.isLike ? 'hearted' : 'heart'" size="20" />
          <em v-if="barCount(barArticle.likeCount)">{{ barCount(barArticle.likeCount) }}</em>
        </span>
        <span class="bar-act" @click="handleFocusComment">
          <svg-icon icon-class="pingluncount" size="20" />
          <em v-if="barCount(barArticle.commentCount)">{{ barCount(barArticle.commentCount) }}</em>
        </span>
        <span
          class="bar-act bar-act--collect"
          :class="{ 'is-on': barArticle.isCollect }"
          @click="handleBarCollect"
        >
          <svg-icon :icon-class="barArticle.isCollect ? 'starred' : 'star'" size="20" />
          <em v-if="barCount(barArticle.collectCount)">{{ barCount(barArticle.collectCount) }}</em>
        </span>
      </div>
    </div>

    <div class="dialog-close-btn" v-if="!isMobile" @click="handleClose">
      <svg-icon icon-class="close" size="48"></svg-icon>
    </div>
  </el-dialog>
</template>

<script>
import ArticleInfo from '@/components/module/article-info/index.vue'
import { userFetch } from '@/api'
import { mapState } from 'vuex'
import { FOLLOW_STATUS, getFollowLabel, resolveFollowStatus } from '@/constant'
import { isVideoArticle } from '@/utils/articleMedia'
export default {
  components: {
    ArticleInfo,
  },
  props: {

    userId: {
      type: String,
      default: '',

    }
  },
  data () {
    return {
      // 文章 id
      articleId: '',
      // 评论 id，用于定位到具体评论
      commentId: '',
      // 控制展示弹窗展示属性
      dialogVisible: false,
      // 数据是否已加载完成
      dataLoaded: false,
      // 模板里要按状态切文案和样式，常量得挂到实例上才能在 template 里引用
      FOLLOW_STATUS,
      // 是否已关注。保留是因为 shouldShowFollowButton 等既有逻辑还在读它，
      // 三态判断请用 followStatus
      isFollowed: false,
      // 关注三态：0 未关注 / 1 已关注 / 2 已申请（对方私密账号，等待同意）
      followStatus: FOLLOW_STATUS.NONE,
      // 只防连点，不驱动按钮 loading
      followActionPending: false,
      // 文章最新数据（用于关闭时同步）
      latestArticleData: null,
      // 文章作者基本信息
      articleUserInfo: {
        // 头像
        avatar: '',
        // 学校
        school: '',
        // 昵称
        nickname: '',
        // 当前文章创建时间
        createTime: '',
        // 用户id
        userId: '',
        // 用户类型 (0: 非匿名, 1: 匿名)
        nickType: 0,
        // 是否通过学校认证
        campusVerified: false
      }
    }
  },
  computed: {
    ...mapState('userStore', ['userInfo']),
    // 移动端走全屏页面式布局（顶部返回栏 + 底部操作条）
    isMobile () {
      return this.$store.state.systemStore.isMobile
    },
    // 底部操作条的数据源。latestArticleData 与 ArticleInfo 内部的 article 是同一个
    // 响应式对象，所以正文里点赞/收藏后这里会自动同步，反之亦然。
    barArticle () {
      return this.latestArticleData || {}
    },
    // 是否为匿名用户 (nickType === 1 表示匿名)
    isAnonymous () {
      return this.articleUserInfo.nickType === 1
    },
    // 关注按钮文案：关注 / 已申请 / 已关注
    followLabel () {
      return getFollowLabel(this.followStatus)
    },
    // 判断是否显示关注按钮
    shouldShowFollowButton () {
      // 数据未加载完成时，不显示按钮（避免闪烁）
      if (!this.dataLoaded) {
        return false
      }
      // 如果是匿名用户，不显示关注按钮
      if (this.isAnonymous) {
        return false
      }
      // 如果是当前登录用户自己，不显示关注按钮
      if (this.userInfo && this.userInfo.id == this.articleUserInfo.userId) {
        return false
      }
      return true
    },
    // 格式化创建时间
    formattedCreateTime () {
      const createTime = this.articleUserInfo.createTime
      if (!createTime) return ''
      
      try {
        // 解析创建时间（Safari 不支持 "yyyy-MM-dd HH:mm:ss" 格式，需要替换为 "yyyy/MM/dd HH:mm:ss"）
        const createDate = new Date(createTime.replace(/-/g, '/'))
        const now = new Date()
        
        // 获取今天的开始时间（00:00:00）
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        // 获取昨天的开始时间
        const yesterdayStart = new Date(todayStart)
        yesterdayStart.setDate(yesterdayStart.getDate() - 1)
        
        // 格式化时间为 HH:mm
        const formatTime = (date) => {
          const hours = String(date.getHours()).padStart(2, '0')
          const minutes = String(date.getMinutes()).padStart(2, '0')
          return `${hours}:${minutes}`
        }
        
        // 判断是否是今天
        if (createDate >= todayStart) {
          return `今天 ${formatTime(createDate)}`
        }
        
        // 判断是否是昨天
        if (createDate >= yesterdayStart && createDate < todayStart) {
          return `昨天 ${formatTime(createDate)}`
        }
        
        // 判断是否是今年
        if (createDate.getFullYear() === now.getFullYear()) {
          const month = String(createDate.getMonth() + 1).padStart(2, '0')
          const day = String(createDate.getDate()).padStart(2, '0')
          return `${month}-${day}`
        }
        
        // 不是今年，显示完整日期
        const year = createDate.getFullYear()
        const month = String(createDate.getMonth() + 1).padStart(2, '0')
        const day = String(createDate.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      } catch (error) {
        console.error('时间格式化失败:', error)
        return createTime
      }
    }
  },
  
  mounted () {
    // 监听关注状态同步事件（同页面）
    this.$root.$on('follow-status-sync', this.handleFollowSync)
    // 监听跨标签页的关注状态同步
    window.addEventListener('storage', this.handleStorageChange)
    // 监听浏览器后退按钮（popstate），移动端点返回关闭弹窗
    window.addEventListener('popstate', this.handlePopState)
  },
  
  beforeDestroy () {
    this.$root.$off('follow-status-sync', this.handleFollowSync)
    window.removeEventListener('storage', this.handleStorageChange)
    window.removeEventListener('popstate', this.handlePopState)
  },

  methods: {
    // 处理关注状态同步
    handleFollowSync (data) {
      // 本弹窗自己的请求还在飞：乐观态已经写过按钮，别被别处的旧广播盖回去
      if (this.followActionPending) return
      // 如果是当前文章作者，更新关注状态
      if (String(this.articleUserInfo.userId) === String(data.userId)) {
        this.isFollowed = data.hasFollower
        // followStatus 缺失时（广播方还没升级）按 hasFollower 推算，
        // 不然会同步成 undefined 让按钮文案退回「关注」
        this.followStatus = resolveFollowStatus(data)
      }
    },
    
    // 处理跨标签页的 storage 变化
    handleStorageChange (e) {
      if (e.key === 'follow-status-sync') {
        try {
          const data = JSON.parse(e.newValue)
          if (String(this.articleUserInfo.userId) === String(data.userId)) {
            this.isFollowed = data.hasFollower
            this.followStatus = resolveFollowStatus(data)
          }
        } catch (err) {
          console.error('解析关注状态失败', err)
        }
      }
    },
    
    // 监听浏览器后退按钮，关闭弹窗而非离开页面
    handlePopState () {
      if (this.dialogVisible && !this._isClosing) {
        // popstate 触发时 history 已经回退了，直接关闭弹窗即可
        // 先标记 dialogVisible 为 false 避免 handleClose 中再次 history.back()
        this.dialogVisible = false
        
        // 通知外部更新文章数据
        if (this.latestArticleData) {
          this.$root.$emit('article-data-sync', {
            id: this.articleId,
            likeCount: this.latestArticleData.likeCount,
            isLike: this.latestArticleData.isLike,
            collectCount: this.latestArticleData.collectCount,
            isCollect: this.latestArticleData.isCollect,
            commentCount: this.latestArticleData.commentCount
          })
        }
        
        // 重置状态
        this.articleId = ''
        this.commentId = ''
        this.dataLoaded = false
        this.isFollowed = false
        this.followStatus = FOLLOW_STATUS.NONE
        this.followActionPending = false
        this.latestArticleData = null
        this.articleUserInfo = {
          avatar: '',
          school: '',
          nickname: '',
          createTime: '',
          userId: '',
          nickType: 0,
          campusVerified: false,
          privateAccount: false
        }
      }
    },
    
    /**
     * 打开帖子详情。
     *
     * @param {String} articleId
     * @param {String} [commentId] 定位到这条评论
     * @param {Object} [preview] 调用方手上已有的帖子数据（列表卡片、带媒体字段的通知）。
     *   是视频帖时直接打开视频播放器，不进图文详情；只有 id 的入口（热榜、老通知）
     *   由 handleArticleLoaded 拉到详情后再转过去
     */
    openDialog (articleId = "", commentId = "", preview = null) {
      if (preview && isVideoArticle(preview) && this.$root.$videoViewer) {
        this.$root.$videoViewer.open(preview, { commentId: commentId || '' })
        return
      }

      // 保存commentId用于定位
      this.commentId = commentId || ''
      
      if (!articleId) {
        console.error('openDialog: articleId is required')
        return
      }
      
      // 重置数据加载状态
      this.dataLoaded = false
      
      // 推入一条 history 记录，这样手机端按返回键会触发 popstate 而非离开页面
      window.history.pushState({ articleDialog: true, articleId }, '', '')
      
      // 只设置 articleId 并打开弹窗，让 ArticleInfo 组件自己获取数据
      this.articleId = articleId
      this.dialogVisible = true
    },
    /**
     * 拉到详情才发现是视频帖（入口只有 id）：换成视频播放器。
     *
     * 弹窗压的那条历史记录直接交给播放器用，不先退再压：history.back() 是异步的，
     * 紧接着 pushState 的话，那次回退会落在播放器刚压的记录上，播放器一打开就被当成「返回」关掉。
     * 详情接口刚记过一次浏览，带 __fromDetail 过去，播放器不再重复拉。
     */
    handOffToVideo (articleData) {
      const commentId = this.commentId
      // 先把 dialogVisible 置 false：随后触发的 close 事件里就不会再 history.back()
      this.dialogVisible = false
      this.articleId = ''
      this.commentId = ''
      this.dataLoaded = false
      this.latestArticleData = null
      this.$root.$videoViewer.open({ ...articleData, __fromDetail: true }, { commentId, reuseHistory: true })
    },
    // 处理 ArticleInfo 组件加载完成后的数据
    handleArticleLoaded (articleData) {
      if (isVideoArticle(articleData) && this.$root.$videoViewer) {
        this.handOffToVideo(articleData)
        return
      }
      const {
        userInfoVO,
        createTime,
      } = articleData

      const {
        avatar,
        nickname,
        id: userId,
        nickType = 0,
        hasFollower = false,
        privateAccount = false
      } = userInfoVO || {}
      
      this.isFollowed = hasFollower
      // /v1/post/info 现在会显式下发 userInfoVO.followStatus（含私密账号的「已申请」），
      // 走 resolveFollowStatus 归一：它仍然兜住只有 hasFollower 的老响应
      this.followStatus = resolveFollowStatus(userInfoVO)
      this.articleUserInfo = {
        avatar,
        // 学校和认证角标取自帖子本身，不再取 userInfoVO：
        // userInfoVO.school 是作者【当前】的资料学校，作者一改教育经历，
        // 历史帖子显示的学校会跟着全变。authorSchoolName 是发帖时快照，
        // authorSchoolVerified 也已由服务端校验过「快照学校 == 作者当前认证学校」。
        school: articleData.authorSchoolName || '',
        nickname,
        createTime,
        userId,
        nickType,
        campusVerified: !!articleData.authorSchoolVerified,
        privateAccount: !!privateAccount
      }
      
      // 保存文章数据
      this.latestArticleData = articleData
      
      // 标记数据已加载完成
      this.dataLoaded = true
    },
    // 处理文章数据更新（点赞、收藏、评论）
    handleArticleUpdated (articleData) {
      this.latestArticleData = articleData
    },

    // ---------- 移动端底部操作条 ----------
    // 0 不显示数字，只留图标，底部条更干净；
    // 进位规则与列表卡片的 formatViews 保持一致（1k / 1w / 1亿）
    barCount (value) {
      if (!value) return ''
      if (value >= 100000000) return (value / 100000000).toFixed(1) + '亿'
      if (value >= 10000) return (value / 10000).toFixed(1) + 'w'
      if (value >= 1000) return (value / 1000).toFixed(1) + 'k'
      return String(value)
    },
    // 复用 ArticleInfo 里的点赞/收藏实现（含接口、toast 与数据回写）
    handleBarLike () {
      const info = this.$refs.articleInfo
      if (!info || !this.latestArticleData) return
      info.like(this.latestArticleData)
    },
    handleBarCollect () {
      const info = this.$refs.articleInfo
      if (!info || !this.latestArticleData) return
      info.handleCollect(this.latestArticleData)
    },
    handleFocusComment () {
      const info = this.$refs.articleInfo
      info && info.focusComment()
    },
    handleClose (done) {
      // 关闭前通知外部更新文章数据
      if (this.latestArticleData) {
        this.$root.$emit('article-data-sync', {
          id: this.articleId,
          likeCount: this.latestArticleData.likeCount,
          isLike: this.latestArticleData.isLike,
          collectCount: this.latestArticleData.collectCount,
          isCollect: this.latestArticleData.isCollect,
          commentCount: this.latestArticleData.commentCount
        })
      }
      
      // 如果弹窗还在显示中（非 popstate 触发），手动回退 history
      if (this.dialogVisible) {
        // 标记正在关闭，避免 popstate 再次触发
        this._isClosing = true
        window.history.back()
        // 使用 setTimeout 确保 popstate 事件已触发后再重置标记
        setTimeout(() => { this._isClosing = false }, 100)
      }
      
      this.articleId = ''
      this.commentId = ''
      this.dialogVisible = false
      this.dataLoaded = false
      this.isFollowed = false
      this.followStatus = FOLLOW_STATUS.NONE
      this.followActionPending = false
      this.latestArticleData = null
      this.articleUserInfo = {
        avatar: '',
        school: '',
        nickname: '',
        createTime: '',
        userId: '',
        nickType: 0,
        campusVerified: false,
        privateAccount: false
      }
      
      // 兼容 before-close 的 done 回调
      if (typeof done === 'function') {
        done()
      }
    },
    // 关注 / 取消关注 / 撤回申请。已申请也走取消分支：再点一次的语义是撤回。
    // 按钮立刻切态，失败回滚，不展示 loading。
    handleFollowToggle () {
      if (this.followActionPending) return
      if (this.followStatus === FOLLOW_STATUS.NONE) {
        this.handleFollowedUser()
      } else {
        this.handleDeleteFollowedUser()
      }
    },
    applyLocalFollowStatus (status) {
      this.followStatus = status
      this.isFollowed = status === FOLLOW_STATUS.FOLLOWED
    },
    broadcastFollowStatus (status) {
      const syncData = {
        userId: this.articleUserInfo.userId,
        hasFollower: status === FOLLOW_STATUS.FOLLOWED,
        followStatus: status
      }
      this.$root.$emit('follow-status-sync', syncData)
      localStorage.setItem('follow-status-sync', JSON.stringify({ ...syncData, timestamp: Date.now() }))
    },
    // 关注用户
    async handleFollowedUser () {
      const prevStatus = this.followStatus
      const optimisticStatus = this.articleUserInfo.privateAccount
        ? FOLLOW_STATUS.PENDING
        : FOLLOW_STATUS.FOLLOWED
      this.followActionPending = true
      this.applyLocalFollowStatus(optimisticStatus)
      try {
        const res = await userFetch.followedUser({ userId: this.articleUserInfo.userId })
        // 对方是私密账号时这里只是提交了申请，还没关注上。
        // 直接置 isFollowed = true 的话按钮会显示成「已关注」，刷新后又变回去。
        const nextStatus = Number(res?.data?.followStatus ?? FOLLOW_STATUS.FOLLOWED)
        const pending = nextStatus === FOLLOW_STATUS.PENDING
        this.applyLocalFollowStatus(nextStatus)
        this.$toast.success(res?.data?.tip || (pending ? '已发送关注申请' : '关注成功'))
        this.broadcastFollowStatus(nextStatus)
      } catch (e) {
        this.applyLocalFollowStatus(prevStatus)
        this.$message.error(e?.message || '关注失败')
      } finally {
        this.followActionPending = false
      }
    },
    // 取消关注用户 / 撤回关注申请
    async handleDeleteFollowedUser () {
      const prevStatus = this.followStatus
      const wasPending = prevStatus === FOLLOW_STATUS.PENDING
      this.followActionPending = true
      this.applyLocalFollowStatus(FOLLOW_STATUS.NONE)
      try {
        await userFetch.deleteFollowedUser(this.articleUserInfo.userId)
        this.$toast.success(wasPending ? '已撤回申请' : '取消关注')
        this.broadcastFollowStatus(FOLLOW_STATUS.NONE)
      } catch (e) {
        this.applyLocalFollowStatus(prevStatus)
        this.$message.error(e?.message || '取消失败')
      } finally {
        this.followActionPending = false
      }
    },
    // 点击作者区域跳转到个人中心
    handleAuthorClick () {
      // 如果是匿名用户，不跳转
      if (this.isAnonymous) {
        return
      }
      // 如果有用户ID，在新标签页打开个人中心
      if (this.articleUserInfo.userId) {
        const routeUrl = this.$router.resolve(`/user/${this.articleUserInfo.userId}`)
        window.open(routeUrl.href, '_blank')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// 与视口尺寸无关的部分：两端都要
::v-deep( .article-dialog) {
  z-index: 3000;
  min-width: auto;
  box-sizing: border-box;

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__header {
    padding: 20px;
    position: sticky;
    top: 0;
    z-index: 999;
  }
}

// 桌面端几何尺寸：必须限定在 min-width 里。
// 这些声明原本是无条件的，而 `::v-deep(.article-dialog)` 编译成
// `[data-v-xxx] .article-dialog`，权重 (0,2,0) 比 mobile.scss 里的
// `.article-dialog` (0,1,0) 高 —— 于是 `height: 100vh !important` 一直在
// 覆盖移动端全屏块，害得移动端不得不再写一遍同样的属性去抢回来。
// 现在按断点划清边界：移动端全屏几何只在 mobile.scss 的 .article-dialog 里定义一处。
@media screen and (min-width: $bp-md) {
  ::v-deep( .article-dialog) {
    margin-top: 0 !important;
    margin-bottom: 0;
    width: 910px;
    max-width: 96vw;
    height: 100vh !important;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
}

.article-dialog-header-room {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  z-index: 100;
  height: 40px;

  .dialog-back-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    margin-left: -6px;
    margin-right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
    -webkit-tap-highlight-color: transparent;

    i {
      font-size: 22px;
      font-weight: 600;
    }

    &:active {
      opacity: 0.5;
    }
  }

  .author-info-wrapper {
    display: flex;
    align-items: center;
    min-width: 0;

    &.clickable {
      cursor: pointer;
    }
  }
  
  .article-dialog-user-avatar {
    width: 40px;
    height: 40px;
    margin-right: 20px;
    border-radius: 100%;
  }
  
  .avatar-placeholder {
    background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  
  .article-dialog-user-info {
    display: flex;
    flex-direction: column;
    .article-dialog-name-text {
      font-size: 16px;
      line-height: 16px;
      color: rgb(51, 51, 51);
      .name-text {
        position: relative;

        .nick-type {
          position: absolute;
          left: calc(100% + 2px);
          top: 0;
        }
      }
    }
    .article-dialog-user-tag {
      align-items: center;
      display: flex;
      height: 12px;
      margin-top: 8px;
      .article-dialog-tag-text {
        font-size: 14px;
        line-height: 14px;
        color: rgba(187, 187, 187, 1);
        margin-right: 10px;
      }
      .article-dialog-tag-text:last-child {
        margin-right: 0 !important;
      }
      .school-with-badge {
        display: inline-flex;
        align-items: center;
        gap: 2px;
      }
    }
  }
}
.article-dialog-content {
  height: calc(100vh - 80px);
  overflow: auto;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

// 桌面端的关闭按钮挂在弹窗右侧外部；移动端不渲染它（改用标题栏里的返回箭头）
.dialog-close-btn {
  position: absolute;
  right: -70px;
  top: 0;
  cursor: pointer;
}

// ---------- 移动端底部操作条 ----------
// 用普通流式布局（弹窗本身是 flex 列容器），不用 position: fixed，
// 这样软键盘弹起时不会被顶到屏幕外，也不会盖住评论输入框。
.article-mobile-bar {
  display: none;
}

.follow-button {
  border-radius: 8px;
  background-color: #ff4757;
  border-color: #ff4757;
  color: #ffffff;
  transition: all 0.3s;
  
  &:hover {
    background-color: #ff6b81;
    border-color: #ff6b81;
  }
  
  &:active {
    background-color: #ee5a6f;
    border-color: #ee5a6f;
  }
  
  // 「已申请」与「已关注」共用弱化样式：都是「已经做过这个动作」，不该再抢注意力
  &.is-followed,
  &.is-pending {
    background-color: #ffffff;
    border-color: #dcdfe6;
    color: #606266;
    
    &:hover {
      background-color: #f5f7fa;
      border-color: #c0c4cc;
      color: #606266;
    }
  }

  // 三种文案宽度差一个字，不给最小宽度按钮会随状态切换跳一下
  min-width: 76px;
  
  .plus-icon {
    font-size: 22px;
    vertical-align: -3px;
    line-height: 4px;
    margin-right: 2px;
  }
}

// 移动端文章弹窗全屏化
// 注意：全屏的几何（position / inset / width / height / margin / border-radius）
// 统一由 src/assets/styles/mobile.scss 的 `.article-dialog` 块负责，这里不再重复声明 ——
// 两处都写同一批属性，就会变成「谁权重高谁赢」的猜谜游戏。这里只管弹窗内部的观感。
@media screen and (max-width: $bp-md - 1px) {
  ::v-deep(.article-dialog) {
    .el-dialog__header {
      padding: 8px 12px !important;
      background: #fff;
      border-bottom: 0.5px solid #f0f0f0;
    }
  }

  .article-dialog-header-room {
    height: 40px;

    .article-dialog-user-avatar {
      width: 34px;
      height: 34px;
      margin-right: 8px;
    }

    .article-dialog-user-info {
      min-width: 0;

      .article-dialog-name-text {
        font-size: 15px;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .article-dialog-user-tag {
        margin-top: 4px;
        .article-dialog-tag-text {
          font-size: 12px;
          margin-right: 8px;
          color: #9a9a9a;
        }
        .school-with-badge {
          display: inline-flex;
          align-items: center;
          gap: 2px;
        }
      }
    }
  }

  // 高度交给弹窗的 flex 列布局（见 mobile.scss 的 .article-dialog），
  // 这里只负责成为唯一的滚动容器
  .article-dialog-content {
    height: auto;
    flex: 1 1 auto;
    min-height: 0;
    border-radius: 0;
  }

  .follow-button {
    height: 30px;
    padding: 0 14px;
    border-radius: 15px;
    font-size: 13px;
  }

  .article-mobile-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
    padding: 8px 12px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
    background: #fff;
    box-shadow: 0 -0.5px 0 rgba(0, 0, 0, 0.08);

    &__write {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      min-width: 0;
      height: 36px;
      padding: 0 14px;
      border-radius: 18px;
      background: #f3f4f6;
      color: #9a9a9a;
      font-size: 14px;
      -webkit-tap-highlight-color: transparent;

      &:active {
        background: #e9eaee;
      }

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    &__acts {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-shrink: 0;
    }

    .bar-act {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      color: #666;
      -webkit-tap-highlight-color: transparent;

      &:active {
        opacity: 0.6;
      }

      em {
        font-style: normal;
        font-size: 12px;
        line-height: 1;
        color: inherit;
      }

      // 图标会换成彩色版本，这里只同步数字颜色
      &.is-on em {
        color: #ff4757;
      }

      &.bar-act--collect.is-on em {
        color: #e6a23c;
      }
    }
  }
}
</style>